import ContentCopy from '@mui/icons-material/ContentCopy';
import QrCode2 from '@mui/icons-material/QrCode2';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Chip, TextField, Typography, useTheme } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { radius } from '~/styles/tokens/shape';
import { copyToClipboard } from '~/utils/copyToClipboard';
import { priceFormatter } from '~/utils/priceFormatter';

interface PixPaymentProps {
  qrCodeValue: string | null;
  pixValue: number;
  onGenerateQRCode: () => void;
  setQrCodeValue: (value: string | null) => void;
  timeLeft: number;
  paymentConfirmed?: boolean;
  disableGenerateButton?: boolean;
  /** Enquanto o QR code é gerado, o botão «Gerar QR code PIX» mostra o carregamento (substitui o toast antigo). */
  generating?: boolean;
}

type CopyStatus = 'idle' | 'copied' | 'error';

const COPY_FEEDBACK_MS = 4000;

export function PixPayment({
  qrCodeValue,
  onGenerateQRCode,
  setQrCodeValue: _setQrCodeValue,
  pixValue,
  timeLeft,
  paymentConfirmed = false,
  disableGenerateButton = false,
  generating = false
}: PixPaymentProps) {
  const theme = useTheme();
  const [isQRCodeVisible, setIsQRCodeVisible] = useState(false);
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');
  const feedbackTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setCopyStatus('idle');
  }, [qrCodeValue]);

  useEffect(() => () => clearTimeout(feedbackTimer.current), []);

  useEffect(() => {
    setIsQRCodeVisible(qrCodeValue !== null && qrCodeValue !== '');
  }, [qrCodeValue]);

  const formatTime = (seconds: number) => {
    if (isNaN(seconds) || seconds < 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const minutesLabel = useMemo(() => Math.max(1, Math.ceil(Math.max(timeLeft, 0) / 60)), [timeLeft]);

  // Chamado direto no clique (gesto do utilizador). Antes usava só `navigator.clipboard.writeText`, que não existe
  // fora de contexto seguro/iframe sem permissão — o clique lançava TypeError e nada era copiado, sem aviso.
  const copyPixCode = async () => {
    if (!qrCodeValue) return;
    const copied = await copyToClipboard(qrCodeValue);
    setCopyStatus(copied ? 'copied' : 'error');
    clearTimeout(feedbackTimer.current);
    if (copied) feedbackTimer.current = setTimeout(() => setCopyStatus('idle'), COPY_FEEDBACK_MS);
  };

  const pillRadius = `${radius.large}px`;
  const tertiaryMain = theme.customColors.tertiaryMain;

  return (
    <Box
      data-app-id="payment-pix-block"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        maxWidth: '100%',
        width: '100%'
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
        <Box alignItems="center" display="flex" flexWrap="wrap" gap={1} justifyContent="space-between">
          <Typography sx={{ fontWeight: 500 }} variant="subtitle1">
            Você tem {minutesLabel} minutos para efetuar o pagamento:
          </Typography>
          {isQRCodeVisible ? (
            <Chip
              color={paymentConfirmed ? 'success' : timeLeft > 0 ? 'success' : 'error'}
              label={paymentConfirmed ? 'Pagamento confirmado' : formatTime(timeLeft)}
              sx={{ fontWeight: 700 }}
              variant="filled"
            />
          ) : null}
        </Box>
        <Typography sx={{ fontWeight: 500 }} variant="subtitle1">
          Valor:{' '}
          <Typography color="primary" component="span" sx={{ fontWeight: 600 }} variant="subtitle1">
            R$ {priceFormatter(pixValue)}
          </Typography>
        </Typography>
      </Box>

      <LoadingButton
        color="primary"
        data-app-id="payment-pix-generate"
        disabled={!!qrCodeValue || paymentConfirmed || disableGenerateButton}
        fullWidth
        loading={generating}
        loadingPosition="start"
        onClick={onGenerateQRCode}
        size="large"
        startIcon={<QrCode2 />}
        sx={{
          borderRadius: pillRadius,
          fontWeight: 600,
          py: 1.5,
          textTransform: 'none'
        }}
        variant="contained"
      >
        Gerar QR code PIX
      </LoadingButton>

      {isQRCodeVisible ? (
        <>
          <Box
            sx={{
              alignItems: 'center',
              alignSelf: 'center',
              bgcolor: '#FFFFFF',
              borderColor: 'divider',
              borderRadius: `${radius.large}px`,
              borderStyle: 'solid',
              borderWidth: 3,
              display: 'flex',
              justifyContent: 'center',
              p: 3
            }}
          >
            <QRCode
              bgColor="#FFFFFF"
              fgColor="#121212"
              size={160}
              value={qrCodeValue || ''}
              style={{ height: 160, width: 160 }}
            />
          </Box>
          <Typography align="center" sx={{ fontWeight: 500 }} variant="subtitle1">
            Escaneie o QR Code com seu app de pagamento.
          </Typography>
        </>
      ) : (
        <Typography align="center" color="text.secondary" variant="body2">
          Clique em &quot;Gerar QR code PIX&quot; para visualizar o código.
        </Typography>
      )}

      <Button
        color="inherit"
        data-app-id="payment-pix-copy"
        disabled={!qrCodeValue || paymentConfirmed}
        fullWidth
        onClick={copyPixCode}
        size="large"
        startIcon={<ContentCopy sx={{ color: tertiaryMain }} />}
        sx={{
          borderColor: tertiaryMain,
          borderRadius: pillRadius,
          borderStyle: 'solid',
          borderWidth: 2,
          color: tertiaryMain,
          fontWeight: 600,
          py: 1.5,
          textTransform: 'none'
        }}
        variant="outlined"
      >
        Copiar código PIX
      </Button>

      {copyStatus === 'copied' ? (
        <Typography
          align="center"
          color="success.main"
          data-app-id="payment-pix-copy-success"
          role="status"
          sx={{ fontWeight: 600 }}
          variant="body2"
        >
          Código PIX copiado!
        </Typography>
      ) : null}
      {copyStatus === 'error' && qrCodeValue ? (
        <Box data-app-id="payment-pix-copy-error" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography align="center" color="error.main" role="alert" sx={{ fontWeight: 600 }} variant="body2">
            Não foi possível copiar automaticamente. Selecione o código abaixo e copie manualmente.
          </Typography>
          <TextField
            fullWidth
            inputProps={{ 'aria-label': 'Código PIX copia e cola', readOnly: true }}
            minRows={2}
            multiline
            onFocus={(event) => event.target.select()}
            size="small"
            value={qrCodeValue}
          />
        </Box>
      ) : null}
    </Box>
  );
}
